import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  "Trouve-moi des contrats de nettoyage commercial dans un rayon de 50 km de Repentigny pour des bureaux ou immeubles de plus de 5 000 pi²",
  "Je cherche des entreprises de Joliette qui pourraient avoir besoin d'un service de nettoyage de bureaux le soir",
  "Appels d'offres publics de nettoyage dans Lanaudière pour les 30 prochains jours",
];

export default function AISearch() {
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/search", { query: q });
      setResult(data);
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  const applyFilters = () => {
    const f = result?.filters || {};
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
    navigate(`/opportunites?${params.toString()}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="ai-search-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-cyan-400" /> Recherche en langage naturel
        </h1>
        <p className="text-muted-foreground mt-1">Décrivez ce que vous cherchez, l'IA convertira votre requête en filtres structurés.</p>
      </div>

      <Card className="p-6 bg-card border-border">
        <Textarea
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Décrivez ce que vous recherchez…"
          rows={4}
          data-testid="ai-search-input"
          className="mb-3 resize-none"
        />
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <Badge key={i} variant="outline" className="cursor-pointer text-[11px] hover:border-cyan-500/50" onClick={() => setQ(ex)} data-testid={`example-${i}`}>
                Ex. {i + 1}
              </Badge>
            ))}
          </div>
          <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={run} disabled={loading} data-testid="ai-search-submit">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Interprétation…</> : <><Sparkles className="h-4 w-4 mr-2" /> Lancer la recherche</>}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-6 bg-card border-border" data-testid="ai-search-result">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Confiance: {result.confidence || "MEDIUM"}</Badge>
          </div>
          <h3 className="font-display font-semibold mb-2">Voici comment j'ai interprété votre recherche</h3>
          <p className="text-sm text-muted-foreground mb-4">{result.interpretation}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(result.filters || {}).map(([k, v]) => (
              v ? (
                <div key={k} className="p-3 rounded-lg bg-secondary/40 border border-border">
                  <div className="text-[10px] uppercase text-muted-foreground font-mono mb-1">{k}</div>
                  <div className="text-sm font-medium truncate">{v}</div>
                </div>
              ) : null
            ))}
          </div>
          <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={applyFilters} data-testid="apply-filters-button">
            Appliquer les filtres <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Card>
      )}
    </div>
  );
}
